import {
  getToken as getFbToken,
  getMessaging,
  isSupported,
  onMessage,
} from 'firebase/messaging'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useFirebaseApp } from 'reactfire'
import { toast } from 'sonner'
import { z } from 'zod'
import { put } from '@/utils/api'
import useUser from '@/hooks/useUser'

const vapidKey =
  'BCYH4lJYo9mGsIDjN07MuE3CaGrLkdhvRgWMHRfUTjpujclg2Yr-kL9Tguv-7Wfp7Vn1fwTDns-4aLtGK1RqSmU'

async function canUseFirebaseMessaging() {
  if (typeof window === 'undefined') return false
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return false
  }
  try {
    return await isSupported()
  } catch (error) {
    console.error(
      '[AppNotifications] Firebase Messaging support check failed:',
      error
    )
    return false
  }
}

const useFirebaseToken = (
  setPermissionToken: (token: string | null | undefined) => void
) => {
  const app = useFirebaseApp()
  const user = useUser()
  const getFirebaseToken = useCallback(async () => {
    try {
      if (!(await canUseFirebaseMessaging())) {
        setPermissionToken(null)
        return
      }
      const messaging = getMessaging(app)
      console.log('Getting token', messaging)
      const currentToken = await getFbToken(messaging, { vapidKey })
      if (currentToken) {
        await put(
          '/tokens',
          { token: currentToken, instanceId: 'browser', user },
          'notifications'
        )
        setPermissionToken(currentToken)
        // You might want to send the token to your server here
      } else {
        console.log(
          'No registration token available. Request permission to generate one.'
        )
        setPermissionToken(null)
      }
    } catch (error) {
      console.error('An error occurred while retrieving token. ', error)
      setPermissionToken(null)
    }
  }, [app, setPermissionToken, user])

  return getFirebaseToken
}

function AppNotifications() {
  const [hasPermission, setHasPermission] = useState(false)
  const setPermissionToken = useCallback(
    (_token: string | null | undefined) => {
      void _token
    },
    []
  )
  const getFirebaseToken = useFirebaseToken(setPermissionToken)
  const [permissionState, setPermissionState] =
    useState<NotificationPermission>('default')
  const push = useNavigate()

  const app = useFirebaseApp()
  const requestNotificationPermission = useCallback(async () => {
    // Standard notification permission request
    console.log('[AppNotifications] Requesting notification permission')
    try {
      if (!(await canUseFirebaseMessaging())) {
        setHasPermission(false)
        setPermissionState('denied')
        return
      }
      const permission = await Notification.requestPermission()
      console.log('[AppNotifications] Permission result:', permission)
      setHasPermission(permission === 'granted')
      setPermissionState(permission)
      if (permission === 'granted') {
        console.log(
          '[AppNotifications] Permission granted, showing success toast'
        )
        toast.success('Stampede notifications enabled', {
          id: 'stampede-notifications-toast-success',
        })
      }
    } catch (error) {
      console.error(
        '[AppNotifications] Error requesting notification permission:',
        error
      )
      setHasPermission(false)
      setPermissionState('denied')
      toast.error('Error requesting notification permission', {
        id: 'stampede-notifications-toast-success',
      })
    }
  }, [])

  useLayoutEffect(() => {
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    canUseFirebaseMessaging().then((supported) => {
      if (cancelled) return
      if (!supported) {
        setPermissionState('denied')
        setHasPermission(false)
        return
      }

      const currentPermission = Notification.permission
      console.log(
        '[AppNotifications] Current notification permission:',
        currentPermission
      )
      setPermissionState(currentPermission)
      setHasPermission(currentPermission === 'granted')

      if (currentPermission === 'default') {
        console.log(
          '[AppNotifications] Permission is default, setting 5s timeout to request'
        )
        timeoutId = setTimeout(() => {
          console.log(
            '[AppNotifications] 5s timeout triggered, requesting permission'
          )
          requestNotificationPermission()
        }, 5000)
      }
    })

    return () => {
      cancelled = true
      if (timeoutId) {
        console.log('[AppNotifications] Cleanup: clearing timeout')
        clearTimeout(timeoutId)
      }
    }
  }, [requestNotificationPermission])

  useEffect(() => {
    if (!hasPermission) return
    getFirebaseToken()
  }, [getFirebaseToken, hasPermission])

  useEffect(() => {
    function handleSWMessage(event: any) {
      console.log('SW: from-client received SW message:', event)

      if (event.data?.type === 'NAVIGATE' && event.data?.url) {
        const url = new URL(event.data.url)
        // e.g. navigate only to the path part:
        push(url.pathname + url.search + url.hash, { replace: true })
      } else if (event.data?.messageType === 'notification-clicked') {
        const url = new URL(event.data.notification.click_action)
        push(url.pathname + url.search + url.hash, { replace: true })
      }
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage)
    }

    // Cleanup on unmount
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage)
      }
    }
  }, [push])

  useEffect(() => {
    if (permissionState !== 'granted') return
    let cancelled = false
    let unsubscribe: (() => void) | null = null

    canUseFirebaseMessaging().then((supported) => {
      if (cancelled || !supported) return
      const messaging = getMessaging(app)
      unsubscribe = onMessage(messaging, (newMessage: any) => {
        console.log('SW: from-server received message:', newMessage)
        const shape = z
          .object({
            notification: z.object({
              title: z.string(),
              body: z.string(),
            }),
            fcmOptions: z.object({
              link: z.string(),
            }),
            messageId: z.string(),
          })
          .safeParse(newMessage)

        if (shape.success) {
          const { notification, fcmOptions } = shape.data

          const url = new URL(fcmOptions.link, window.location.origin)

          toast.info(notification.title, {
            description: notification.body,
            action: {
              onClick: () => {
                push(url.pathname + url.search + url.hash, { replace: true })
              },
              label: 'View',
            },
          })
        } else {
          console.log('SW: from-client error', shape.error)
        }
      })
    })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [app, permissionState, push])

  return null
}

export default AppNotifications
